import { useState } from 'react';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

const AGREEMENT_LABEL = 'I have read and agree to the guide agreement.';

interface GuideAgreementConsentProps {
  /** Public path of the current agreement PDF. */
  url: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /**
   * This profile already accepted the current version. The box is usable
   * immediately — they are not asked to open the PDF again.
   */
  alreadyAccepted: boolean;
  showError: boolean;
  /** Drop the card chrome when the block sits inside another section. */
  embedded?: boolean;
}

/**
 * Confirmation shown under the profile form. The checkbox stays disabled
 * until the guide opens the PDF, unless this version was already accepted.
 */
export function GuideAgreementConsent({
  url,
  checked,
  onCheckedChange,
  alreadyAccepted,
  showError,
  embedded = false,
}: GuideAgreementConsentProps) {
  const [opened, setOpened] = useState(alreadyAccepted);
  const canAgree = opened || alreadyAccepted;

  const body = (
    <>
      {!embedded && (
        <>
          <Typography sx={{ color: '#102747', fontSize: 16, lineHeight: 1.25, fontWeight: 800 }}>
            Guide agreement
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.35, fontSize: 12.5 }}>
            Read the agreement, then confirm it before you submit your profile.
          </Typography>
        </>
      )}

      <Link
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => setOpened(true)}
        sx={{ display: 'inline-block', mt: embedded ? 0 : 1.5, fontSize: 13.5, fontWeight: 700 }}
      >
        Read the guide agreement (PDF)
      </Link>

      <FormControl error={showError} sx={{ display: 'block', mt: 1.25 }}>
        <FormControlLabel
          sx={{
            alignItems: 'flex-start',
            mx: 0,
            gap: 0.75,
            '& .MuiCheckbox-root': { p: 0, mt: '1px' },
          }}
          control={
            <Checkbox
              checked={checked}
              disabled={!canAgree}
              size="small"
              onChange={(event) => onCheckedChange(event.target.checked)}
            />
          }
          label={
            <Typography component="span" sx={{ fontSize: 13, color: '#304158', lineHeight: 1.45 }}>
              {AGREEMENT_LABEL}
            </Typography>
          }
        />
        {!canAgree && (
          <FormHelperText sx={{ mx: 0 }}>
            Open the agreement before you can agree to it.
          </FormHelperText>
        )}
        {showError && (
          <FormHelperText sx={{ mx: 0 }}>
            Please accept the guide agreement before submitting.
          </FormHelperText>
        )}
      </FormControl>
    </>
  );

  if (embedded) return body;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        mb: 2,
        border: '1px solid',
        borderColor: showError ? 'error.light' : '#e3e8ef',
        borderRadius: 2.5,
      }}
    >
      {body}
    </Paper>
  );
}
